import { FC, Fragment, useEffect } from 'react'
import { DateTime } from 'luxon'

import ScrollTo from '@/components/ScrollTo'
import Message from './components/Message'
import { IMessage, StoreContextType } from '@/context/types'
import {
  CONVERSATION_STATE,
  DIRECTION,
  SCROLL_TARGET_ID,
} from '@/context/constants'
import Loading from '@/components/Loading'
import ChatBubble from '@/components/ChatBubble'

import { StoreContext, useMessenger } from '..'
import { isConversationExpired } from '@/common/utils'

import './styles.css'

export interface ChatThreadProps {
  messages: IMessage[]
  className?: string
  isChatWidget: boolean
  context?: React.Context<StoreContextType | null>
}

const ChatThread: FC<ChatThreadProps> = ({
  messages,
  className,
  isChatWidget = false,
  context = StoreContext,
}) => {
  const { getLoading, getSelectedConversation, findConversation, getIsTyping } =
    useMessenger(context)
  const conversation = findConversation(getSelectedConversation())
  const isExpired = isConversationExpired(conversation)
  const isTyping = getIsTyping()

  useEffect(() => {
    const targetElement = document.getElementById(SCROLL_TARGET_ID)
    if (targetElement && messages.length) {
      targetElement.scrollIntoView({ behavior: 'auto' })
    }
  }, [messages])

  const renderDateHeader = (
    message: IMessage,
    prevMessage: IMessage,
    index: Number,
  ) => {
    let dateHeader = DateTime.fromISO(message.received).toFormat('MMM dd, yyyy')

    // If not first message and same day
    // return null
    if (
      prevMessage &&
      DateTime.fromISO(prevMessage.received)
        .startOf('day')
        .hasSame(DateTime.fromISO(message.received).startOf('day'), 'day')
    ) {
      return null
    }

    if (
      index === 0 &&
      conversation?.metadata?.state === CONVERSATION_STATE.NEW
    ) {
      return null
    }

    return (
      <div className="pmui-flex pmui-justify-center pmui-w-full pmui-text-sm pmui-my-3 pmui-text-pop-gray-400">
        {dateHeader}
      </div>
    )
  }

  const renderMessages = () => {
    if (conversation?.metadata?.state === CONVERSATION_STATE.NEW && isExpired) {
      return (
        <div className="pmui-flex pmui-flex-col pmui-items-center pmui-justify-center pmui-w-full pmui-h-full">
          <h1 className="pmui-text-base pmui-my-3 pmui-font-semibold pmui-text-pop-gray">
            Session has already expired
          </h1>
          <p className="pmui-text-sm pmui-text-pop-gray-200 text-center">
            Please create a new message
          </p>
        </div>
      )
    }

    if (messages.length > 0) {
      return (
        <div className="pmui-overflow-x-hidden pmui-overflow-y-auto">
          {messages.map((message: IMessage, index: number) => {
            if (message.deleted || message.metadata?.isHiddenInChat) {
              return null
            }

            return (
              <Fragment key={message.id}>
                {renderDateHeader(message, messages[index - 1], index)}
                <Message
                  message={message}
                  isChatWidget={isChatWidget}
                  context={context}
                />
              </Fragment>
            )
          })}
          {isTyping && (
            <>
              <ChatBubble
                direction={DIRECTION.LEFT}
                classname="typing-chat-bubble mb-4"
              >
                <div id="typing" className="dot-flashing pmui-ml-2" />
              </ChatBubble>
              <ScrollTo targetId={SCROLL_TARGET_ID} />
            </>
          )}
          <ScrollTo targetId={SCROLL_TARGET_ID} />
        </div>
      )
    }
    return null
  }

  return (
    <div className={className}>
      {getLoading().messages ? <Loading context={context} /> : renderMessages()}
    </div>
  )
}

export default ChatThread
